/* eslint-disable react-refresh/only-export-components */

// DEPENDENCIES
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onSnapshot, setDoc } from "@firebase/firestore";
import Countdown from "react-countdown-now";

// TYPES
import { FormikProps, FormikValues } from "formik";
import { ComponentStep, OnboardingType } from "../../types";
import { AppDispatch, RootState, store } from "../../app/store";

// UTILS
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getDraftUUIDFromToken,
  goToStep,
  isTokenActive,
} from "../../app/utils";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";
import { regenerateToken } from "../../connectors/connectors";
import {
  genericToastifyError,
  getEmailValidationRef,
  getPartnerCompanyType,
  minutesToMiliseconds,
  physicalEmailVerificationAuthorize,
  physicalEmailVerificationSuccess,
} from "../../utils";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { Alert } from "@allaria-tech/matter/Alert";
import { Icon } from "@allaria-tech/matter/Icon";
import useWindowWidth from "../../hooks/useWindowWidth";
import { getCompanyData } from "../../utils/companies";
import { persistor } from "../../main";
import { onUpdateTheme } from "../../reducers/activeTemplateReducer";

import EmailTokenCard from "../../components/EmailValidation/EmailTokenCard";

import styles from "../../components/EmailValidation/EmailValidation.module.scss";
import { Typography } from "@allaria-tech/matter/Typography";

const RESEND_TEXT = "Reenviar mail";
const RESEND_COOLDOWN = minutesToMiliseconds(2);

export interface EmailValidationData {
  verified: boolean;
  onboardingType: OnboardingType;
}

interface EmailValidationProps {
  email: string;
  onEditEmail: () => void | Promise<void>;
  onBack: () => void;
  onResend: () => Promise<boolean>;
  onTokenAuthorize: (draftUUID?: string) => Promise<void>;
  onTokenSuccess: () => void | Promise<void>;
}

const emailValidationStep = ({
  step,
  onAuthorize,
  onSuccess,
}: {
  step: number;
  onAuthorize?: (
    draftUUID: string,
    dispatch: AppDispatch,
    formik: FormikProps<FormikValues>,
    state: RootState,
  ) => Promise<void>;
  onSuccess?: (
    dispatch: AppDispatch,
    state: RootState,
    formik: FormikProps<FormikValues>,
  ) => void | Promise<void>;
}): Omit<ComponentStep, "step"> => ({
  label: "emailValidation",
  name: "Mail",
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    const { formValues } = state.onboardingData;
    const { signatureRequestId } = state.authentication;

    const email = formik.values?.email || formValues?.email;

    const onResend = async (): Promise<boolean> => {
      try {
        if (!signatureRequestId || !email)
          throw new Error("Error: signatureRequestId or email not found");

        const partnerCompanyType =
          formValues && getPartnerCompanyType(formValues);

        await regenerateToken({
          email,
          signatureRequestId,
          companyType: getCompanyData().companyName,
          es: true,
          ...(partnerCompanyType ? { partnerCompanyType } : {}),
        });

        showToastMessage("Te reenviamos el email de validación.", {
          containerId: "layoutPage",
          type: "SUCCESS",
        });

        return true;
      } catch (error) {
        console.error(error);
        showToastMessage(genericToastifyError, {
          containerId: "layoutPage",
          type: "ERROR",
        });

        return false;
      }
    };

    const onEditEmail = () => goToStep({ dispatch, step: step - 1 });

    const onBack = () => goToStep({ dispatch, step: step - 1 });

    // Manual-code authorize/success wiring for the Matter TokenCard modal. Same
    // logic the standalone token step uses: run the custom handler when provided,
    // otherwise fall back to the default physical email-verification flow.
    const onTokenAuthorize = async (draftUUID?: string) => {
      try {
        if (!draftUUID) throw new Error("Error: draftUUID not found");

        if (onAuthorize) {
          return await onAuthorize(draftUUID, dispatch, formik, state);
        }

        await physicalEmailVerificationAuthorize(
          draftUUID,
          dispatch,
          formik,
          state,
        );
      } catch (error) {
        console.log(error, "Error on authorize");
        Sentry.captureException(error);
      }
    };

    const onTokenSuccess = async () => {
      if (onSuccess) {
        return await onSuccess(dispatch, state, formik);
      }

      await physicalEmailVerificationSuccess(dispatch, formik, step);
    };

    return (
      <EmailValidation
        email={email}
        onEditEmail={onEditEmail}
        onBack={onBack}
        onResend={onResend}
        onTokenAuthorize={onTokenAuthorize}
        onTokenSuccess={onTokenSuccess}
      />
    );
  },
  disableBackNavigation: true,
  hideStepperOnMobile: true,
  hideHeaderOnMobile: true,
});

const isAuthenticatedForDraft = (
  token?: string | null,
  draftUUID?: string | null,
) => {
  if (!token || !draftUUID) return false;
  try {
    return (
      isTokenActive({ token }) &&
      getDraftUUIDFromToken({ token }) === draftUUID
    );
  } catch {
    return false;
  }
};

const EmailValidation = ({
  email,
  onEditEmail,
  onBack,
  onResend,
  onTokenAuthorize,
  onTokenSuccess,
}: EmailValidationProps) => {
  const { emailValidationContainer, header, bodyGroup, links, link } = styles;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { width } = useWindowWidth();
  const isMobile = width < 768;
  const { onboardingType, theme } = useAppSelector(
    (state) => state.activeTemplate,
  );
  const { signatureRequestId } = useAppSelector(
    (state) => state.authentication,
  );
  const [isTokenModalOpen, setIsTokenModalOpen] = useState(false);
  const isResumeBootRef = useRef(
    new URLSearchParams(window.location.search).has("preload"),
  );
  const mountedStepRef = useRef(store.getState().onboardingData.stepData.step);
  const [isResending, setIsResending] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(
    () => Date.now() + RESEND_COOLDOWN,
  );
  const [countdown, setCountdown] = useState({
    minutes: 2,
    seconds: 0,
    completed: false,
  });

  const onResendClick = async () => {
    setIsResending(true);
    const success = await onResend();
    if (success) setResendAvailableAt(Date.now() + RESEND_COOLDOWN);
    setIsResending(false);
  };

  const emailValidationRef = signatureRequestId
    ? getEmailValidationRef(signatureRequestId)
    : null;

  useEffect(() => {
    if (!emailValidationRef) return;

    const unsub = onSnapshot(emailValidationRef, async (doc) => {
      if (doc.exists()) {
        const data = doc.data() as EmailValidationData;
        if (data.verified) {
          const { authentication, onboardingData } = store.getState();
          if (
            isResumeBootRef.current ||
            onboardingData.stepData.step !== mountedStepRef.current ||
            isAuthenticatedForDraft(
              authentication.token,
              onboardingData.draftUUID,
            )
          )
            return;

          persistor.pause();
          dispatch({ type: "reset-store" });
          if (theme) dispatch(onUpdateTheme(theme));
          navigate("/verificacion-email?status=SUCCESS");
        }
      } else {
        await setDoc(emailValidationRef, {
          verified: false,
          onboardingType,
        });
      }
    });
    return unsub;
  }, []);

  const onCountdownChange = ({
    minutes,
    seconds,
    completed,
  }: {
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => setCountdown({ minutes, seconds, completed });

  useEffect(() => {
    const { minutes, seconds, completed } = countdown;
    dispatch(
      onUpdatePrimaryButton({
        text: completed
          ? RESEND_TEXT
          : `${RESEND_TEXT} ${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`,
        type: "button",
        onClick: onResendClick,
        disabled: !completed || isResending,
        isLoading: isResending,
        show: true,
      }),
    );
    dispatch(
      onUpdateSecondaryButton({
        text: "Ingresar código",
        type: "button",
        onClick: () => setIsTokenModalOpen(true),
        disabled: false,
        isLoading: false,
        show: isMobile,
      }),
    );
    dispatch(
      onUpdateTertiaryButton({
        text: "Volver",
        type: "button",
        onClick: onBack,
        disabled: false,
        isLoading: false,
        show: isMobile,
      }),
    );
  }, [countdown, isResending, isMobile]);

  return (
    <div className={emailValidationContainer}>
      <Countdown
        date={resendAvailableAt}
        key={resendAvailableAt}
        onMount={onCountdownChange}
        onTick={onCountdownChange}
        onComplete={onCountdownChange}
        renderer={() => null}
      />
      <div className={header}>
        {isMobile ? (
          <Icon name="warning-filled" size={80} />
        ) : (
          <Icon name="pending-filled" size="lg" />
        )}
        <Typography type="HEADING" size={isMobile ? "M" : "L"}>
          Verificá tu email para continuar
        </Typography>
      </div>
      <div className={bodyGroup}>
        {isMobile ? (
          <>
            <Typography type="BODY" size="S">
              Enviamos un email a {email}. Abrilo y seguí el enlace para
              confirmar tu dirección.
            </Typography>
            <Typography type="BODY" size="S">
              El enlace es válido durante 24 horas.
            </Typography>
          </>
        ) : (
          <>
            <Typography type="BODY" size="M">
              Enviamos un email a{" "}
              <Typography type="BODY" size="M" weight="EMPHASIS" tag="span">
                {email}
              </Typography>
              .
            </Typography>
            <Typography type="BODY" size="M">
              Abrilo y verificá tu email desde el enlace. El enlace es válido
              durante 24 horas.
            </Typography>
          </>
        )}
      </div>
      {!isMobile && (
        <Alert variant="INFO" width="FIT">
          Si la verificación la realizás desde tu celular, vas a continuar la
          apertura desde ese dispositivo.
        </Alert>
      )}
      {!isMobile && (
        <div className={links}>
          <Typography type="BODY" size="S">
            Si no funciona el enlace podés{" "}
            <button
              type="button"
              className={link}
              onClick={() => setIsTokenModalOpen(true)}
            >
              ingresar el código
            </button>{" "}
            manualmente. ¿No recibiste el correo? Podés{" "}
            <button type="button" className={link} onClick={onEditEmail}>
              modificar el email
            </button>{" "}
            o reenviarlo
          </Typography>
        </div>
      )}
      <EmailTokenCard
        isOpen={isTokenModalOpen}
        onClose={() => setIsTokenModalOpen(false)}
        onAuthorize={onTokenAuthorize}
        onSuccess={onTokenSuccess}
      />
    </div>
  );
};

export default emailValidationStep;
